import React, { useCallback, useMemo } from 'react';
import { PAGED_SCHEDULE_TYPE_CELL_PARAM_TEMPLATES_QUERY } from '../queries';
import { Card } from '@totalsoft/rocket-ui';
import { getListBrowserStore } from '@ts-private/list-browser';
import ListAltIcon from '@mui/icons-material/ListAlt';
import TableListBrowser from 'features/common/components/table-list-browser/TableListBrowser';
import ApolloFetchingComponent from 'features/common/components/apollo-fetching/ApolloFetchingComponent';
import ScheduleTypeCellParamTemplateListItem from './ScheduleTypeCellParamTemplateListItem';
import { useMutation } from 'hooks/apollo/useMutation';
import { DELETE_SCHEDULE_TYPE_CELL_PARAM_TEMPLATE } from '../mutations';
import { SCHEDULE_TYPE_CELL_PARAM_TEMPLATE_DELETED } from '../subscriptions';
import { useToast } from 'hooks/toasts/useToast';
import { useTranslation } from 'react-i18next';
import PropTypes from 'prop-types';
import useSubscription from 'hooks/apollo/useSubscription';

export const scheduleTypeCellParamTemplatesStore = getListBrowserStore({
  filterDefinitions: [
    {
      name: 'templateName',
      caption: 'ScheduleTypeCellParamTemplate.TemplateName',
      type: 'text'
    },
    {
      name: 'activeYN',
      caption: 'ScheduleTypeCellParamTemplate.ActiveYN',
      type: 'boolean'
    }
  ],
  orderByFields: [
    {
      caption: 'ScheduleTypeCellParamTemplate.TemplateName',
      value: 'TemplateName'
    }
  ],
  pager: {
    orderBy: 'TemplateName',
    direction: 'asc'
  }
});

const ScheduleTypeCellParamTemplateListBrowser = ({ canWrite }) => {
  const addToast = useToast();
  const [t] = useTranslation();

  const fetchingOptions = useMemo(
    () => ({
      queryOptions: {
        query: PAGED_SCHEDULE_TYPE_CELL_PARAM_TEMPLATES_QUERY,
        name: 'pagedScheduleTypeCellParamTemplates'
      }
    }),
    []
  );

  const [deleteScheduleTypeCellParamTemplate] = useMutation(DELETE_SCHEDULE_TYPE_CELL_PARAM_TEMPLATE);

  const handleDelete = useCallback(
    scheduleTypeCellParamTemplateId => {
      if (window.confirm(t('ScheduleTypeCellParamTemplate.DeleteScheduleTypeCellParamTemplate'))) {
        deleteScheduleTypeCellParamTemplate({ variables: { input: { scheduleTypeCellParamTemplateId: scheduleTypeCellParamTemplateId } } });
      }
    },
    [deleteScheduleTypeCellParamTemplate, t]
  );

  useSubscription(SCHEDULE_TYPE_CELL_PARAM_TEMPLATE_DELETED, {
    onData: ({ client }) => {
      addToast(t('ScheduleTypeCellParamTemplate.ScheduleTypeCellParamTemplateDeleted'), 'success');
      client.cache.evict({ id: 'ROOT_QUERY', fieldName: 'pagedScheduleTypeCellParamTemplates' });
      client.cache.gc();
    }
  });

  const itemComponent = useCallback(
    ({ data }) => <ScheduleTypeCellParamTemplateListItem data={data} handleDelete={handleDelete} canWrite={canWrite} />,
    [handleDelete, canWrite]
  );

  const columns = useMemo(
    () => [
      { name: 'templateName', caption: 'ScheduleTypeCellParamTemplate.TemplateName' },
      { name: 'activeYN', caption: 'ScheduleTypeCellParamTemplate.ActiveYN' },
      { name: 'actions', caption: 'Actions', width: '120px' }
    ],
    []
  );

  return (
    <Card icon={ListAltIcon}>
      <TableListBrowser
        name='schedule-type-cell-param-templates-browser'
        store={scheduleTypeCellParamTemplatesStore}
        ItemComponent={itemComponent}
        columns={columns}
        layout='fixed'
      />
      <ApolloFetchingComponent store={scheduleTypeCellParamTemplatesStore} options={fetchingOptions} />
    </Card>
  );
};

ScheduleTypeCellParamTemplateListBrowser.propTypes = {
  canWrite: PropTypes.bool.isRequired
};

export default ScheduleTypeCellParamTemplateListBrowser;
